import { useLeftMenuStore } from '@/stores/leftMenuStore.js';
import { useTopMenuStore } from '@/stores/topMenuStore.js';

// 1. Scan for the generic "boot.js" file in every package
const packageBootFiles = import.meta.glob('/packages/**/resources/js/boot.js', { eager: true });

/**
 * Bootstraps all discovered packages.
 * @param {object} appInstances - Contains { app, pinia, router, i18n }
 */
export function bootstrapPackages({ app, pinia, router, i18n }) {

  // Prepare the stores once, so we can pass helper functions if we want
  const leftMenuStore = useLeftMenuStore(pinia);
  const topMenuStore = useTopMenuStore(pinia);

  // 2. Define the "Context" object.
  // This is the toolbox your packages will receive.
  const context = {
    // A. Direct Access (Power User)
    app,
    pinia,
    router,
    i18n,

    // B. Helper Methods (Syntactic Sugar)
    // This makes the package code cleaner and less coupled to specific store implementations
    addLeftMenuItem: (item) => leftMenuStore.addTopLevelItem(item),
    addLeftMenuChild: (parentId, item) => leftMenuStore.addChildItem(parentId, item),
    addTopMenuItem: (item) => topMenuStore.addTopLevelItem(item),

    /**
     * @param {string} locale - 'en', 'fr'
     * @param {object} messages - The translation object
     * @param {string} [namespace] - Optional: Force a prefix
     */
    addTranslations: (locale, messages, namespace = null) => {
      if (i18n?.global) {
        // If a namespace is provided, wrap the messages in it
        const finalMessages = namespace ? { [namespace]: messages } : messages;
        i18n.global.mergeLocaleMessage(locale, finalMessages);
      }
    },
    /**
     * Registers permission labels into a GLOBAL 'permissions' scope.
     * * Usage: addPermissionTranslations('en', { 'users view': 'View Users' })
     * Result: $t('permissions.users view') -> "View Users"
     * * @param {string} locale - 'en', 'fr', etc.
     * @param locale
     * @param {object} messages - Flat object: { 'raw permission': 'Readable Label' }
     */
    addPermissionTranslations: (locale, messages) => {
      if (i18n && i18n.global) {
        // We forcibly wrap these messages in a 'permissions' key.
        // This ensures all packages merge into the same global bucket.
        i18n.global.mergeLocaleMessage(locale, {
          permissions: messages
        });
      } else {
        console.warn(`[PackageLoader] Cannot add permission translations. i18n missing.`);
      }
    }
  };

  // 3. Loop and Execute
  for (const path in packageBootFiles) {
    const module = packageBootFiles[path];

    if (module.default && typeof module.default === 'function') {
      try {
        // Execute the package's bootstrap function
        module.default(context);
      } catch (e) {
        console.error(`[PackageLoader] Error bootstrapping package at ${path}:`, e);
      }
    }
  }
}
